import { useCourses } from 'core/hooks/course/useCourses';
import { useGroups } from 'core/hooks/useGroups';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { userDataService } from '../../services';

type SelectOption = {
  label: string;
  value: string;
};

type UseTeacherAIAnalyticsPanelReturn = {
  allCourseIds: string[];
  classOptions: SelectOption[];
  courses: any[];
  groupCourseMap: Record<string, string[]>;
  groups: any;
  loading: boolean;
  selectedClass: SelectOption;
  selectedTimePeriod: SelectOption;
  setSelectedClass: (option: SelectOption) => void;
  setSelectedTimePeriod: (option: SelectOption) => void;
  timePeriodOptions: SelectOption[];
};

function useTeacherAIAnalyticsPanel(): UseTeacherAIAnalyticsPanelReturn {
  const { t } = useTranslation(['chatbot']);
  const { groups } = useGroups();
  const { courses } = useCourses();

  const [aiCourses, setAiCourses] = useState<Array<{ id: string }> | null>(null);
  const [aiCoursesLoading, setAiCoursesLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    const fetchCourses = async () => {
      setAiCoursesLoading(true);
      try {
        const data = await userDataService.getCourses();
        if (!cancelled) setAiCourses(data);
      } catch (error) {
        if (!cancelled) setAiCourses([]);
      } finally {
        if (!cancelled) setAiCoursesLoading(false);
      }
    };
    fetchCourses();
    return () => {
      cancelled = true;
    };
  }, []);

  const validAiCourseIds: Set<string> = useMemo(() => {
    if (!aiCourses) return new Set();
    return new Set(aiCourses.map((c) => c.id));
  }, [aiCourses]);

  const allClassesOption: SelectOption = useMemo(
    () => ({
      label: t('chatbot:ai_analytics_all_classes'),
      value: 'all',
    }),
    [t]
  );

  const classOptions: SelectOption[] = useMemo(() => {
    const schoolGroups = groups?.schoolGroups || [];
    const groupOptions = schoolGroups.map((group: { name: string; guid: string }) => ({
      label: group.name,
      value: group.guid,
    }));
    return [allClassesOption, ...groupOptions];
  }, [groups, allClassesOption]);

  const [selectedClass, setSelectedClass] = useState<SelectOption>(allClassesOption);

  const timePeriodOptions: SelectOption[] = useMemo(
    () => [
      { label: t('chatbot:ai_analytics_period_last_week'), value: '7d' },
      { label: t('chatbot:ai_analytics_period_last_month'), value: '30d' },
      { label: t('chatbot:ai_analytics_period_last_3_months'), value: '3m' },
    ],
    [t]
  );

  const [selectedTimePeriod, setSelectedTimePeriod] = useState<SelectOption>(timePeriodOptions[0]);

  const groupGuids = useMemo(() => {
    const schoolGroups = groups?.schoolGroups || [];
    return schoolGroups.map((g: { guid: string }) => g.guid);
  }, [groups]);

  const groupCourseMap: Record<string, string[]> = useMemo(() => {
    if (!courses || groupGuids.length === 0 || validAiCourseIds.size === 0) return {};
    const map: Record<string, string[]> = {};
    groupGuids.forEach((gid: string) => {
      map[gid] = courses
        .filter((c: { school_group_guid: string; guid: string }) => c.school_group_guid === gid && validAiCourseIds.has(c.guid))
        .map((c: { guid: string }) => c.guid);
    });
    return map;
  }, [courses, groupGuids, validAiCourseIds]);

  const allCourseIds: string[] = useMemo(() => {
    return Object.values(groupCourseMap).flat();
  }, [groupCourseMap]);

  const loading = !groups || !courses || aiCoursesLoading;

  return {
    allCourseIds,
    classOptions,
    courses: courses || [],
    groupCourseMap,
    groups,
    loading,
    selectedClass,
    selectedTimePeriod,
    setSelectedClass,
    setSelectedTimePeriod,
    timePeriodOptions,
  };
}

export default useTeacherAIAnalyticsPanel;
